import React from 'react'
import PropTypes from 'prop-types'
import Label from './Label'
import Input from './Input'
import Control from './Control'
import Field from './Field'

/* doc
---
title: InputField
name: InputField
category: React - Bulma
parent: Field
---

An `InputField` is a wrapper for combining `Field` and `Input` components.

Use this if you need to add an Input field to a form.

See [Bulma Documentation](http://bulma.io/documentation/form/input/) for reference and full options.
If one of the Bulma options isn't listed below, it may be added using the `className` prop.

### Props
| Prop Name       | Description                                   |Type            | Default Value             | Required?     |
|-----------------|-----------------------------------------------|----------------|---------------------------|---------------|
| name            | Name/id for input field                       | string         |                           | yes           |
| value           | defaultValue for input                        | any            |                           | no            |
| type            | Type of input                                 | string         | 'text'                    | no            |
| size            | Size of input                                 | string         |                           | no            |
| label           | Label's display text                          | string         |                           | no            |
| fieldProps      | Props for `Field` component                   | object         | {}                        | no            |
| labelProps      | Props for `Label` component                   | object         | {}                        | no            |
| controlProps    | Props for `Control` component                 | object         | {}                        | no            |
| inputProps      | Props for `Input` component                   | object         | {}                        | no            |


```html_example
  <InputField
    name="example"
    value="starting value"
    label="Example Input Field"
  />
```
*/

/**
* InputField Component. Used to maintain consistency for html/css regarding
* input fields.
*
* @TODO Add support for input colors/states.
* @TODO Add support for icons.
* @TODO Add support for help text.
* @TODO Create individual components for each input type.
*/
function InputField(props) {
  const {
    onChange, label, name, value, type, size, fieldProps, labelProps, controlProps, inputProps, ...rest
  } = props

  return (
    <Field {...fieldProps} {...rest}>
      {label ? <Label htmlFor={name} size={size} {...labelProps}>{label}</Label> : false}
      <Control {...controlProps}>
        <Input
          {...inputProps}
          onChange={onChange}
          name={name}
          value={value}
          inputSize={size}
          type={type}
        />
      </Control>
    </Field>
  )
}

InputField.defaultProps = {
  type: 'text',
  fieldProps: {},
  labelProps: {},
  controlProps: {},
  inputProps: {},
}

InputField.propTypes = {
  // Field name/id
  name: PropTypes.string.isRequired,
  // defaultValue for input.
  value: PropTypes.any,
  // Input type.
  type: PropTypes.string,
  // Size of input: "small", "medium", "large"
  size: PropTypes.string,
  // Label display text.
  label: PropTypes.string,
  // Props for `Field` component.
  fieldProps: PropTypes.object,
  // Props for `Label` component.
  labelProps: PropTypes.object,
  // Props for `Control` component.
  controlProps: PropTypes.object,
  // Props for `Input` component.
  inputProps: PropTypes.object,
}

export default InputField
